import { motion } from "framer-motion";
import { useLanguage } from "@/components/sybrax/LanguageProvider";
import { content } from "@/components/sybrax/i18n";

const Team = () => {
    const { lang } = useLanguage();
    const c = content[lang].team;

    return (
        <section
            id="team"
            data-testid="team-section"
            className="relative py-24 sm:py-32 overflow-hidden"
        >
            <div className="pointer-events-none absolute -bottom-20 left-1/4 w-[420px] h-[420px] rounded-full bg-cyan-500/[0.06] blur-[120px]" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="max-w-3xl"
                >
                    <span className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.25em] text-cyan-600 dark:text-cyan-400">
                        <span className="mr-3 h-px w-8 bg-cyan-500/70 dark:bg-cyan-400/60" />
                        {c.label}
                    </span>
                    <h2
                        data-testid="team-title"
                        className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white"
                    >
                        {c.title}
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                        {c.intro}
                    </p>
                </motion.div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                    {c.members.map((m, i) => (
                        <motion.article
                            key={m.name}
                            data-testid={`team-card-${i}`}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{
                                duration: 0.6,
                                ease: [0.22, 1, 0.36, 1],
                                delay: i * 0.08,
                            }}
                            className="glass-card p-8 flex flex-col h-full group"
                        >
                            <div className="relative w-24 h-24 mx-auto">
                                <div
                                    className={`absolute inset-0 rounded-full bg-gradient-to-br ${m.gradient} blur-xl opacity-70 group-hover:opacity-100 transition-opacity`}
                                />
                                <div className="relative w-full h-full rounded-full bg-slate-100 dark:bg-[#0a101d] border border-slate-200 dark:border-white/10 flex items-center justify-center shadow-inner">
                                    <span className="font-display text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                                        {m.initials}
                                    </span>
                                </div>
                            </div>

                            <h3
                                data-testid={`team-name-${i}`}
                                className="mt-6 font-display text-xl font-semibold text-slate-900 dark:text-white text-center"
                            >
                                {m.name}
                            </h3>
                            <p className="mt-1 text-center text-xs font-semibold uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400">
                                {m.role}
                            </p>
                            <p className="mt-5 text-sm text-slate-600 dark:text-slate-400 leading-relaxed text-center flex-1">
                                {m.description}
                            </p>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Team;
